/**
 * /settings — notification preferences (M6.1).
 *
 * Matrix of NotificationEvent (rows) × NotificationChannel (columns) with
 * checkboxes. Each toggle is sent to PUT /me/notification-preferences
 * with an optimistic update so the UI feels instant.
 *
 * Future settings (timezone, name, avatar) can join this page when needed.
 */
import { Fragment, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  Smartphone,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  type NotificationChannel,
  type NotificationEvent,
  type NotificationPreference,
} from '@vari-crew/schemas';
import { useQuery } from '@tanstack/react-query';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '@/api/notification-preferences';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { apiCall } from '@/lib/api';
import { useLaborRates, useUpdateLaborRates } from '@/api/labor-rates';
import { useCostSettings, useUpdateCostSettings } from '@/api/cost-settings';
import { useReminderSettings, useUpdateReminderSettings } from '@/api/reminder-settings';
import type { LaborRates, ReminderSettingsUpdateInput } from '@vari-crew/schemas';
import { WarehousesCard } from '@/components/settings/WarehousesCard';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { CopyButton } from '@/components/ui/CopyButton';
import { cn } from '@/lib/cn';

const RATE_ROWS: { key: keyof LaborRates; label: string; hint: string }[] = [
  { key: 'lead', label: 'Lead', hint: 'Crew lead' },
  { key: 'installer', label: 'Installer', hint: 'Install crew' },
  { key: 'driver', label: 'Driver', hint: 'Pickup / delivery' },
  { key: 'trainee', label: 'Trainee', hint: 'Trainee crew' },
  {
    key: 'estimate_default',
    label: 'Default (intake estimates)',
    hint: "Used before crew is assigned — the role mix isn't known yet",
  },
];

/**
 * Bid cost settings (admin/supervisor) — the mileage rate and the debris cost
 * per quarter-truck that drive the bid calculator (§A). Edited in-app so the
 * IRS mileage rate can change without a deploy.
 */
function CostSettingsCard() {
  const { data, isLoading, error } = useCostSettings();
  const update = useUpdateCostSettings();
  const [mileage, setMileage] = useState<string | null>(null);
  const [debris, setDebris] = useState<string | null>(null);

  const mileageVal = mileage ?? (data ? (data.mileage_per_mile_cents / 100).toFixed(2) : '');
  const debrisVal = debris ?? (data ? (data.debris_per_quarter_cents / 100).toFixed(2) : '');
  const dirty = mileage !== null || debris !== null;
  const perQuarter = Number(debrisVal) || 0;

  function save() {
    const patch: Record<string, number> = {};
    const m = Math.round(Number(mileageVal) * 100);
    const d = Math.round(Number(debrisVal) * 100);
    if (Number.isFinite(m) && m >= 0) patch.mileage_per_mile_cents = m;
    if (Number.isFinite(d) && d >= 0) patch.debris_per_quarter_cents = d;
    update.mutate(patch, {
      onSuccess: () => {
        setMileage(null);
        setDebris(null);
        toast.success('Cost settings updated');
      },
      onError: (e) => toast.error(`Failed: ${e.message}`),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Bid cost settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-ink-dim">
          The mileage rate and debris cost used by the bid calculator. Update the
          mileage rate when the IRS rate changes.
        </p>
        {isLoading && <div className="text-sm text-ink-faint">Loading…</div>}
        {error && (
          <div className="text-sm text-status-blocked-fg" role="alert">
            Couldn&apos;t load settings ({(error as Error).message}).
          </div>
        )}
        {data && (
          <>
            <div className="space-y-2 max-w-md">
              <label className="flex items-center gap-3 text-sm">
                <span className="w-48 shrink-0">
                  Mileage rate
                  <span className="block text-[11px] text-ink-faint">round-trip miles × this</span>
                </span>
                <span className="text-ink-faint">$</span>
                <input
                  type="number"
                  min={0}
                  step={0.01}
                  aria-label="Mileage rate, dollars per mile"
                  value={mileageVal}
                  onChange={(e) => setMileage(e.target.value)}
                  className="w-24 h-8 rounded border border-line bg-transparent px-2 text-sm"
                />
                <span className="text-[11px] text-ink-faint">/ mi</span>
              </label>
              <label className="flex items-center gap-3 text-sm">
                <span className="w-48 shrink-0">
                  Debris removal
                  <span className="block text-[11px] text-ink-faint">per quarter-truck</span>
                </span>
                <span className="text-ink-faint">$</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  aria-label="Debris cost, dollars per quarter truck"
                  value={debrisVal}
                  onChange={(e) => setDebris(e.target.value)}
                  className="w-24 h-8 rounded border border-line bg-transparent px-2 text-sm"
                />
                <span className="text-[11px] text-ink-faint">/ ¼ truck</span>
              </label>
              <p className="text-[11px] text-ink-faint">
                Debris tiers: ¼ ${perQuarter.toFixed(0)} · ½ ${(perQuarter * 2).toFixed(0)} · ¾ $
                {(perQuarter * 3).toFixed(0)} · full ${(perQuarter * 4).toFixed(0)}
              </p>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={save} disabled={update.isPending || !dirty}>
                {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save settings
              </Button>
              {dirty && (
                <button
                  type="button"
                  onClick={() => {
                    setMileage(null);
                    setDebris(null);
                  }}
                  className="text-xs text-ink-faint hover:text-ink underline"
                >
                  Reset
                </button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Crew labor rates (admin only) — per-role $/hr used to cost actual labor in
 * the margin report, and a blended default for the intake estimate. Editing a
 * rate takes effect immediately; no deploy needed.
 */
function LaborRatesCard() {
  const { data, isLoading, error } = useLaborRates();
  const update = useUpdateLaborRates();
  // Local dollar-string edits, committed on Save.
  const [draft, setDraft] = useState<Record<string, string> | null>(null);

  const rows = draft ?? null;
  function dollarsFor(key: keyof LaborRates): string {
    if (rows && key in rows) return rows[key]!;
    return data ? (data[key] / 100).toFixed(2) : '';
  }
  function setDollars(key: keyof LaborRates, v: string) {
    setDraft((d) => ({
      ...(d ?? Object.fromEntries(RATE_ROWS.map((r) => [r.key, data ? (data[r.key] / 100).toFixed(2) : '']))),
      [key]: v,
    }));
  }
  function save() {
    if (!rows) return;
    const patch = {} as Record<string, number>;
    for (const r of RATE_ROWS) {
      const cents = Math.round(Number(rows[r.key]) * 100);
      if (Number.isFinite(cents) && cents >= 0) patch[r.key] = cents;
    }
    update.mutate(patch, {
      onSuccess: () => {
        setDraft(null);
        toast.success('Labor rates updated');
      },
      onError: (e) => toast.error(`Failed: ${e.message}`),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Crew labor rates</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-ink-dim">
          Hourly cost per crew role. Actual labor in the margin report is costed
          at each assigned crew member's role rate; the intake estimate uses the
          blended default below.
        </p>
        {isLoading && <div className="text-sm text-ink-faint">Loading rates…</div>}
        {error && (
          <div className="text-sm text-status-blocked-fg" role="alert">
            Couldn&apos;t load rates ({(error as Error).message}).
          </div>
        )}
        {data && (
          <>
            <div className="space-y-2 max-w-md">
              {RATE_ROWS.map((r) => (
                <label key={r.key} className="flex items-center gap-3 text-sm">
                  <span className="w-48 shrink-0">
                    {r.label}
                    <span className="block text-[11px] text-ink-faint">{r.hint}</span>
                  </span>
                  <span className="text-ink-faint">$</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    aria-label={`${r.label} rate, dollars per hour`}
                    value={dollarsFor(r.key)}
                    onChange={(e) => setDollars(r.key, e.target.value)}
                    className="w-24 h-8 rounded border border-line bg-transparent px-2 text-sm"
                  />
                  <span className="text-[11px] text-ink-faint">/ hr</span>
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={save} disabled={update.isPending || !rows}>
                {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save rates
              </Button>
              {rows && (
                <button
                  type="button"
                  onClick={() => setDraft(null)}
                  className="text-xs text-ink-faint hover:text-ink underline"
                >
                  Reset
                </button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/** A compact on/off switch (matches the notification-matrix toggle style). */
function MiniToggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className={cn(
        'w-9 h-5 rounded-full border transition-colors relative shrink-0',
        on ? 'bg-status-complete-fg border-status-complete-fg' : 'bg-fill border-line',
      )}
    >
      <span
        className={cn(
          'absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-bg rounded-full transition-all shadow-sm',
          on ? 'left-[18px]' : 'left-0.5',
        )}
      />
    </button>
  );
}

/** Days/hours unit dropdown for a reminder's lead/interval value. */
function UnitSelect({
  value,
  onChange,
  disabled,
}: {
  value: 'days' | 'hours';
  onChange: (v: 'days' | 'hours') => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as 'days' | 'hours')}
      disabled={disabled}
      aria-label="Reminder unit"
      className="h-8 rounded border border-line bg-bg px-1.5 text-sm disabled:opacity-50"
    >
      <option value="days">days</option>
      <option value="hours">hours</option>
    </select>
  );
}

/**
 * Reminder cadence (supervisor + admin) — controls the reminder sweep (runs
 * hourly): which reminders fire, how far ahead, and in what unit (incl. the
 * task-ending crew nudge). Backed by /reminders/settings.
 */
function ReminderSettingsCard() {
  const { data, isLoading, error } = useReminderSettings();
  const update = useUpdateReminderSettings();
  const [draft, setDraft] = useState<ReminderSettingsUpdateInput | null>(null);
  const [regText, setRegText] = useState<string | null>(null);

  const cur = data ? { ...data, ...(draft ?? {}) } : null;
  const dirty = draft !== null || regText !== null;
  const set = (patch: ReminderSettingsUpdateInput) => setDraft((d) => ({ ...(d ?? {}), ...patch }));

  function save() {
    const patch: ReminderSettingsUpdateInput = { ...(draft ?? {}) };
    if (regText !== null) {
      patch.registration_lead_days = regText
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0);
    }
    update.mutate(patch, {
      onSuccess: () => {
        setDraft(null);
        setRegText(null);
        toast.success('Reminder settings updated');
      },
      onError: (e) => toast.error(`Failed: ${e.message}`),
    });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reminder cadence</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-ink-dim">
          The reminder sweep runs hourly. Turn any reminder off, or change how far ahead it
          fires — in days or hours.
        </p>
        {isLoading && <div className="text-sm text-ink-faint">Loading…</div>}
        {error && (
          <div className="text-sm text-status-blocked-fg" role="alert">
            Couldn&apos;t load reminder settings ({(error as Error).message}).
          </div>
        )}
        {cur && (
          <>
            <div className="space-y-3 max-w-lg">
              <div className="flex items-center gap-3">
                <MiniToggle on={cur.upcoming_job_enabled} onChange={(v) => set({ upcoming_job_enabled: v })} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Upcoming job</div>
                  <div className="text-[11px] text-ink-faint">Heads-up before a scheduled job.</div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-ink-dim">
                  <input
                    type="number"
                    min={0}
                    max={90}
                    value={cur.upcoming_job_lead_days}
                    onChange={(e) => set({ upcoming_job_lead_days: Number(e.target.value) })}
                    disabled={!cur.upcoming_job_enabled}
                    aria-label="Upcoming job lead value"
                    className="w-14 h-8 rounded border border-line bg-transparent px-2 text-sm disabled:opacity-50"
                  />
                  <UnitSelect
                    value={cur.upcoming_job_lead_unit}
                    onChange={(u) => set({ upcoming_job_lead_unit: u })}
                    disabled={!cur.upcoming_job_enabled}
                  />
                  before
                </label>
              </div>

              <div className="flex items-center gap-3">
                <MiniToggle on={cur.pickup_reminder_enabled} onChange={(v) => set({ pickup_reminder_enabled: v })} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Warehouse pickup</div>
                  <div className="text-[11px] text-ink-faint">Heads-up before a pickup date.</div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-ink-dim">
                  <input
                    type="number"
                    min={0}
                    max={90}
                    value={cur.pickup_reminder_lead_days}
                    onChange={(e) => set({ pickup_reminder_lead_days: Number(e.target.value) })}
                    disabled={!cur.pickup_reminder_enabled}
                    aria-label="Pickup reminder lead value"
                    className="w-14 h-8 rounded border border-line bg-transparent px-2 text-sm disabled:opacity-50"
                  />
                  <UnitSelect
                    value={cur.pickup_reminder_lead_unit}
                    onChange={(u) => set({ pickup_reminder_lead_unit: u })}
                    disabled={!cur.pickup_reminder_enabled}
                  />
                  before
                </label>
              </div>

              <div className="flex items-center gap-3">
                <MiniToggle on={cur.registration_reminder_enabled} onChange={(v) => set({ registration_reminder_enabled: v })} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Vehicle registration</div>
                  <div className="text-[11px] text-ink-faint">Alert at each of these days before it lapses.</div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-ink-dim">
                  <input
                    type="text"
                    value={regText ?? cur.registration_lead_days.join(', ')}
                    onChange={(e) => setRegText(e.target.value)}
                    disabled={!cur.registration_reminder_enabled}
                    placeholder="30, 14, 7"
                    aria-label="Registration reminder day thresholds"
                    className="w-24 h-8 rounded border border-line bg-transparent px-2 text-sm disabled:opacity-50"
                  />
                  days
                </label>
              </div>

              <div className="flex items-center gap-3">
                <MiniToggle on={cur.daily_summary_enabled} onChange={(v) => set({ daily_summary_enabled: v })} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Daily dispatch summary</div>
                  <div className="text-[11px] text-ink-faint">Morning roll-up of today&apos;s jobs (ops).</div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <MiniToggle
                  on={cur.task_ending_reminder_enabled}
                  onChange={(v) => set({ task_ending_reminder_enabled: v })}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Task ending soon</div>
                  <div className="text-[11px] text-ink-faint">
                    Nudge assigned crew before a task&apos;s scheduled end to finish or extend.
                  </div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-ink-dim whitespace-nowrap">
                  <input
                    type="number"
                    min={1}
                    max={120}
                    value={cur.task_ending_reminder_lead_minutes}
                    onChange={(e) =>
                      set({ task_ending_reminder_lead_minutes: Number(e.target.value) })
                    }
                    disabled={!cur.task_ending_reminder_enabled}
                    aria-label="Task ending reminder lead minutes"
                    className="w-14 h-8 rounded border border-line bg-transparent px-2 text-sm disabled:opacity-50"
                  />
                  min before
                </label>
              </div>

              <div className="flex items-center gap-3">
                <MiniToggle
                  on={cur.request_approval_reminder_enabled}
                  onChange={(v) => set({ request_approval_reminder_enabled: v })}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Incoming request approval</div>
                  <div className="text-[11px] text-ink-faint">
                    Re-nudge supervisors while a request is unapproved.
                  </div>
                </div>
                <label className="flex items-center gap-1 text-xs text-ink-dim whitespace-nowrap">
                  every
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={cur.request_approval_reminder_interval_days}
                    onChange={(e) =>
                      set({ request_approval_reminder_interval_days: Number(e.target.value) })
                    }
                    disabled={!cur.request_approval_reminder_enabled}
                    aria-label="Request approval reminder interval"
                    className="w-12 h-8 rounded border border-line bg-transparent px-1.5 text-sm disabled:opacity-50"
                  />
                  <UnitSelect
                    value={cur.request_approval_reminder_interval_unit}
                    onChange={(u) => set({ request_approval_reminder_interval_unit: u })}
                    disabled={!cur.request_approval_reminder_enabled}
                  />
                  , up to
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={cur.request_approval_reminder_max}
                    onChange={(e) => set({ request_approval_reminder_max: Number(e.target.value) })}
                    disabled={!cur.request_approval_reminder_enabled}
                    aria-label="Request approval reminder max count"
                    className="w-12 h-8 rounded border border-line bg-transparent px-1.5 text-sm disabled:opacity-50"
                  />
                  ×
                </label>
              </div>

              <div className="flex items-center gap-3">
                <MiniToggle
                  on={cur.bid_approval_reminder_enabled}
                  onChange={(v) => set({ bid_approval_reminder_enabled: v })}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">Bid / date approval</div>
                  <div className="text-[11px] text-ink-faint">
                    Re-nudge the requestor while a sent approval is pending.
                  </div>
                </div>
                <label className="flex items-center gap-1 text-xs text-ink-dim whitespace-nowrap">
                  every
                  <input
                    type="number"
                    min={1}
                    max={90}
                    value={cur.bid_approval_reminder_interval_days}
                    onChange={(e) =>
                      set({ bid_approval_reminder_interval_days: Number(e.target.value) })
                    }
                    disabled={!cur.bid_approval_reminder_enabled}
                    aria-label="Bid approval reminder interval"
                    className="w-12 h-8 rounded border border-line bg-transparent px-1.5 text-sm disabled:opacity-50"
                  />
                  <UnitSelect
                    value={cur.bid_approval_reminder_interval_unit}
                    onChange={(u) => set({ bid_approval_reminder_interval_unit: u })}
                    disabled={!cur.bid_approval_reminder_enabled}
                  />
                  , up to
                  <input
                    type="number"
                    min={0}
                    max={10}
                    value={cur.bid_approval_reminder_max}
                    onChange={(e) => set({ bid_approval_reminder_max: Number(e.target.value) })}
                    disabled={!cur.bid_approval_reminder_enabled}
                    aria-label="Bid approval reminder max count"
                    className="w-12 h-8 rounded border border-line bg-transparent px-1.5 text-sm disabled:opacity-50"
                  />
                  ×
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button size="sm" onClick={save} disabled={update.isPending || !dirty}>
                {update.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Save settings
              </Button>
              {dirty && (
                <button
                  type="button"
                  onClick={() => {
                    setDraft(null);
                    setRegText(null);
                  }}
                  className="text-xs text-ink-faint hover:text-ink underline"
                >
                  Reset
                </button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Outlook subscription URL (PHASE-2 C.2): a signed, personal ICS feed —
 * PMs and Leo see the crew schedule in the calendar they already live in.
 */
function CalendarFeedCard() {
  const { data, isLoading, error } = useQuery<{ url: string }>({
    queryKey: ['me', 'calendar-feed'],
    queryFn: () => apiCall<{ url: string }>('/me/calendar-feed'),
    staleTime: Infinity,
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Outlook calendar</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p className="text-ink-dim">
          Subscribe to your Vari FMS schedule in Outlook: Calendar → Add
          calendar → Subscribe from web, then paste this address. It stays in
          sync automatically (Outlook refreshes it periodically).
        </p>
        {isLoading && <div className="text-ink-faint">Preparing your feed link…</div>}
        {error && (
          <div className="text-status-blocked-fg" role="alert">
            Couldn&apos;t mint a feed link ({(error as Error).message}).
          </div>
        )}
        {data && (
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate rounded border border-line bg-fill-soft px-2 py-1.5 text-xs">
              {data.url}
            </code>
            <CopyButton value={data.url} label="Copy calendar feed URL" />
          </div>
        )}
        <p className="text-[11px] text-ink-faint">
          The link is personal — it shows the schedule your role can see.
          Don&apos;t share it outside Vari.
        </p>
      </CardContent>
    </Card>
  );
}

const EVENT_LABELS: Record<NotificationEvent, { label: string; sub: string }> = {
  project_scheduled: {
    label: 'Project scheduled',
    sub: 'A new project lands on the calendar.',
  },
  project_approval_requested: {
    label: 'Approval requested',
    sub: 'A PM submitted a new project request.',
  },
  project_approved: {
    label: 'Project approved',
    sub: 'Your project request was approved.',
  },
  crew_assignment_changed: {
    label: 'Crew assignment changed',
    sub: "You're added or removed from a task.",
  },
  en_route: {
    label: 'Crew en route',
    sub: 'Crew has left the warehouse / previous site.',
  },
  on_site: {
    label: 'Crew on site',
    sub: 'Crew has arrived at the project.',
  },
  task_started: {
    label: 'Work started',
    sub: 'A crew started work (in progress) on a task.',
  },
  task_completed: {
    label: 'Task completed',
    sub: 'A crew marked a task or step complete.',
  },
  project_finished: {
    label: 'Project finished',
    sub: 'A crew finished a project’s field work — review the recap and close it out.',
  },
  task_ending_soon: {
    label: 'Task ending soon',
    sub: 'Reminder to assigned crew before a task’s scheduled end.',
  },
  project_starting_soon: {
    label: 'Project starting soon',
    sub: 'Reminder to assigned crew ~10 min before a project’s committed start.',
  },
  project_ending_soon: {
    label: 'Project ending soon',
    sub: 'Reminder to assigned crew ~10 min before a project’s committed end.',
  },
  pickup_starting_soon: {
    label: 'Pickup starting soon',
    sub: 'Reminder to the crew on a pickup ~10 min before its scheduled start.',
  },
  punch_walkthrough_ready: {
    label: 'Punch / walkthrough ready',
    sub: 'Install finished; PM can review.',
  },
  complete_recap_available: {
    label: 'Project complete · recap available',
    sub: 'Closeout PDF ready to share.',
  },
  blocked_needs_attention: {
    label: 'Blocked · needs attention',
    sub: 'Project is stuck pending a decision.',
  },
  note_added: {
    label: 'Note added',
    sub: 'Someone posts a note on a task or the project.',
  },
  approval_changes_requested: {
    label: 'Changes requested',
    sub: 'An approver pushes back on a sent bid instead of approving.',
  },
  project_changes_requested: {
    label: 'Request sent back',
    sub: 'A supervisor sends your request back with changes to make.',
  },
  project_rescheduled: {
    label: 'Rescheduled',
    sub: 'A project you’re on moves to a different day or time.',
  },
  task_requested: {
    label: 'Task requested',
    sub: 'A PM requested a task on a project — confirm it to schedule the work.',
  },
  task_confirmed: {
    label: 'Task confirmed',
    sub: 'A task you requested was confirmed into scheduled work.',
  },
  access_requested: {
    label: 'Access request',
    sub: 'Someone requested a Vari FMS account (admins approve).',
  },
  task_cancelled: {
    label: 'Task cancelled',
    sub: 'A scheduled task you’re on was cancelled or removed.',
  },
  blocked_resolved: {
    label: 'Unblocked',
    sub: 'A blocked project you’re on is back on track.',
  },
  vehicle_issue_reported: {
    label: 'Vehicle issue reported',
    sub: 'Crew flagged a defect on a fleet vehicle.',
  },
  vehicle_out_of_service: {
    label: 'Vehicle out of service',
    sub: 'A vehicle was taken out of service — affects dispatch.',
  },
  vehicle_issue_resolved: {
    label: 'Vehicle issue resolved',
    sub: 'An issue you reported on a vehicle was fixed.',
  },
  punch_item_added: {
    label: 'Punch item added',
    sub: 'A punch-list item was flagged on a project.',
  },
  punch_item_updated: {
    label: 'Punch item updated',
    sub: 'A punch-list item was edited or removed.',
  },
  punch_item_assigned: {
    label: 'Punch item assigned',
    sub: 'A punch-list item was assigned to you to fix.',
  },
  punch_item_resolved: {
    label: 'Punch item resolved',
    sub: 'A punch-list item was closed out.',
  },
  document_added: {
    label: 'Document added',
    sub: 'A sales order, packing slip, or design package was added to a project.',
  },
  crew_message_mention: {
    label: 'Crew chat messages',
    sub: 'A new message in a task’s crew-only chat — you get these for any task you’re assigned to, or when @mentioned.',
  },
  upcoming_job: {
    label: 'Upcoming job reminder',
    sub: 'A heads-up before a job you’re scheduled on.',
  },
  pickup_reminder: {
    label: 'Pickup reminder',
    sub: 'A heads-up before a warehouse pickup date.',
  },
  registration_expiring: {
    label: 'Vehicle registration due',
    sub: 'A vehicle’s registration or inspection is coming due.',
  },
  daily_dispatch_summary: {
    label: 'Daily dispatch summary',
    sub: 'A morning roll-up of the day’s scheduled jobs.',
  },
  request_approval_reminder: {
    label: 'Approval reminder (incoming request)',
    sub: 'A nudge while a request awaits your approval.',
  },
  bid_approval_reminder: {
    label: 'Approval reminder (bid/date sign-off)',
    sub: 'A nudge while your bid or dates await approval.',
  },
};

const CHANNEL_META: Record<
  NotificationChannel,
  { label: string; sub: string; Icon: typeof Mail }
> = {
  teams: { label: 'Teams', sub: 'Adaptive Card', Icon: MessageSquare },
  email: { label: 'Email', sub: 'SendGrid', Icon: Mail },
  sms: { label: 'SMS', sub: 'Twilio', Icon: Phone },
  in_app: { label: 'In-app', sub: 'Toast + bell', Icon: AlertTriangle },
  push: { label: 'Push', sub: 'Web push', Icon: Smartphone },
};

// Only the channels we actually deliver on (recipients.ts ACTIVE_CHANNELS). SMS +
// web push are wired but held off, so their columns are hidden rather than shown
// as dead toggles.
const ACTIVE_CHANNELS: NotificationChannel[] = ['teams', 'email', 'in_app'];

// Events organized into sections for the Settings matrix (each event in one group).
const EVENT_GROUPS: { title: string; events: NotificationEvent[] }[] = [
  {
    title: 'My schedule & assignments',
    events: [
      'project_scheduled',
      'project_rescheduled',
      'crew_assignment_changed',
      'task_confirmed',
      'task_cancelled',
      'upcoming_job',
      'pickup_reminder',
    ],
  },
  {
    title: 'Requests & approvals',
    events: [
      'project_approval_requested',
      'project_approved',
      'approval_changes_requested',
      'project_changes_requested',
      'task_requested',
    ],
  },
  {
    title: 'Live job status',
    events: [
      'en_route',
      'on_site',
      'task_started',
      'task_completed',
      'punch_walkthrough_ready',
      'complete_recap_available',
      'blocked_needs_attention',
      'blocked_resolved',
    ],
  },
  {
    title: 'Punch list & quality',
    events: ['punch_item_added', 'punch_item_updated', 'punch_item_assigned', 'punch_item_resolved'],
  },
  { title: 'Collaboration', events: ['note_added', 'document_added'] },
  {
    title: 'Fleet',
    events: [
      'vehicle_issue_reported',
      'vehicle_out_of_service',
      'vehicle_issue_resolved',
      'registration_expiring',
    ],
  },
  { title: 'Account & admin', events: ['access_requested'] },
  { title: 'Digests', events: ['daily_dispatch_summary'] },
];

// Crew see a focused subset — the events that actually reach them (recipients.ts).
const CREW_EVENTS: NotificationEvent[] = [
  'project_scheduled',
  'project_rescheduled',
  'crew_assignment_changed',
  'task_cancelled',
  'upcoming_job',
  'pickup_reminder',
  'blocked_needs_attention',
  'blocked_resolved',
  'punch_item_assigned',
  'note_added',
  'document_added',
  'vehicle_issue_resolved',
];

function lookupKey(event: NotificationEvent, channel: NotificationChannel) {
  return `${event}::${channel}`;
}

export function Settings() {
  const { data, isLoading, error } = useNotificationPreferences();
  const update = useUpdateNotificationPreferences();
  const { data: me } = useCurrentUser();
  const isCrew = me?.role === 'crew';

  // Pending optimistic toggles, keyed by event::channel, mapped to enabled bool
  const [pending, setPending] = useState<Record<string, boolean>>({});

  const prefMap = useMemo(() => {
    const map = new Map<string, NotificationPreference>();
    if (!data) return map;
    for (const p of data.data) map.set(lookupKey(p.event, p.channel), p);
    return map;
  }, [data]);

  function isEnabled(event: NotificationEvent, channel: NotificationChannel): boolean {
    const key = lookupKey(event, channel);
    if (key in pending) return pending[key]!;
    return prefMap.get(key)?.enabled ?? false;
  }

  function toggle(event: NotificationEvent, channel: NotificationChannel) {
    const key = lookupKey(event, channel);
    const next = !isEnabled(event, channel);
    setPending((p) => ({ ...p, [key]: next }));
    update.mutate([{ event, channel, enabled: next }], {
      onSuccess: () => {
        setPending((p) => {
          const { [key]: _omit, ...rest } = p;
          return rest;
        });
      },
      onError: (err) => {
        toast.error(`Failed: ${err.message}`);
        setPending((p) => {
          const { [key]: _omit, ...rest } = p;
          return rest;
        });
      },
    });
  }

  const channels = ACTIVE_CHANNELS;
  // Group the events; for crew keep only their focused subset, and drop any group
  // that ends up empty for this role.
  const crewSet = isCrew ? new Set<NotificationEvent>(CREW_EVENTS) : null;
  const groups = EVENT_GROUPS.map((g) => ({
    title: g.title,
    events: crewSet ? g.events.filter((e) => crewSet.has(e)) : g.events,
  })).filter((g) => g.events.length > 0);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">Settings</h1>
        <p className="text-sm text-ink-dim">
          {isCrew
            ? 'Choose how you hear about assignment and schedule changes. Teams and in-app alerts activate as soon as the bot is connected.'
            : "Notification preferences, grouped by area. Defaults are tuned per role — flip anything you'd rather not hear about. Teams, email, and in-app deliver today; SMS and push are coming soon."}
        </p>
      </header>

      <CalendarFeedCard />

      {(me?.role === 'admin' || me?.role === 'supervisor') && <LaborRatesCard />}

      {(me?.role === 'admin' || me?.role === 'supervisor') && <CostSettingsCard />}

      {(me?.role === 'admin' || me?.role === 'supervisor') && <WarehousesCard />}

      {(me?.role === 'admin' || me?.role === 'supervisor') && <ReminderSettingsCard />}

      {isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Loader2 className="w-5 h-5 animate-spin inline-block text-ink-faint" />
          </CardContent>
        </Card>
      )}
      {error && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-status-blocked-fg">
            {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {data && data.data.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-ink-dim">
            You don't have notification preferences yet. They're seeded for
            every user at sign-up; if you're seeing this, your account
            probably isn't fully provisioned in the users table.
          </CardContent>
        </Card>
      )}

      {data && data.data.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {/* Desktop: the full event × channel matrix. */}
            <div className="hidden md:block overflow-x-auto">
            <table className={cn('w-full text-sm', !isCrew && 'min-w-[720px]')}>
              <thead>
                <tr className="border-y border-line bg-fill-soft">
                  <th className="text-left px-4 py-2 font-medium text-xs uppercase tracking-wide text-ink-faint">
                    Event
                  </th>
                  {channels.map((c) => {
                    const meta = CHANNEL_META[c];
                    return (
                      <th
                        key={c}
                        className="px-2 py-2 font-medium text-xs uppercase tracking-wide text-ink-faint text-center min-w-[88px]"
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <meta.Icon className="w-3.5 h-3.5" />
                          <span>{meta.label}</span>
                          <span className="text-[10px] text-ink-faint normal-case tracking-normal">
                            {meta.sub}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {groups.map((g) => (
                  <Fragment key={g.title}>
                    <tr>
                      <td
                        colSpan={channels.length + 1}
                        className="px-4 pt-5 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint"
                      >
                        {g.title}
                      </td>
                    </tr>
                    {g.events.map((e) => {
                      const meta = EVENT_LABELS[e];
                      return (
                        <tr key={e} className="border-b border-line last:border-0">
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium">{meta.label}</div>
                            <div className="text-xs text-ink-dim mt-0.5">{meta.sub}</div>
                          </td>
                          {channels.map((c) => {
                            const enabled = isEnabled(e, c);
                            const isPending = lookupKey(e, c) in pending;
                            return (
                              <td key={c} className="px-2 py-3 text-center">
                                <button
                                  type="button"
                                  onClick={() => toggle(e, c)}
                                  disabled={isPending}
                                  className={cn(
                                    'w-9 h-5 rounded-full border transition-colors relative',
                                    enabled
                                      ? 'bg-status-complete-fg border-status-complete-fg'
                                      : 'bg-fill border-line',
                                    isPending && 'opacity-60',
                                  )}
                                  aria-pressed={enabled}
                                  title={`${meta.label} via ${CHANNEL_META[c].label}: ${enabled ? 'on' : 'off'}`}
                                >
                                  <span
                                    className={cn(
                                      'absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-bg rounded-full transition-all shadow-sm',
                                      enabled ? 'left-[18px]' : 'left-0.5',
                                    )}
                                  />
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </Fragment>
                ))}
              </tbody>
            </table>
            </div>

            {/* Mobile: one card per event with channel chip-toggles, so every
                toggle is reachable without a sideways-scrolling table. */}
            <div className="md:hidden">
              {groups.map((g) => (
                <div key={g.title} className="border-t border-line first:border-t-0">
                  <div className="px-4 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
                    {g.title}
                  </div>
                  <div className="divide-y divide-line">
                    {g.events.map((e) => {
                      const meta = EVENT_LABELS[e];
                      return (
                        <div key={e} className="px-4 py-3">
                          <div className="font-medium">{meta.label}</div>
                          <div className="text-xs text-ink-dim mt-0.5">{meta.sub}</div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {channels.map((c) => {
                              const cmeta = CHANNEL_META[c];
                              const enabled = isEnabled(e, c);
                              const isPending = lookupKey(e, c) in pending;
                              return (
                                <button
                                  key={c}
                                  type="button"
                                  onClick={() => toggle(e, c)}
                                  disabled={isPending}
                                  aria-pressed={enabled}
                                  title={`${meta.label} via ${cmeta.label}: ${enabled ? 'on' : 'off'}`}
                                  className={cn(
                                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors',
                                    enabled
                                      ? 'bg-status-complete-fg text-bg border-status-complete-fg'
                                      : 'bg-bg text-ink-dim border-line',
                                    isPending && 'opacity-60',
                                  )}
                                >
                                  <cmeta.Icon className="w-3.5 h-3.5" />
                                  {cmeta.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-4 py-3 text-[11px] text-ink-faint border-t border-line">
              Changes save automatically. Only Teams, email, and in-app deliver today
              (SMS and push are coming soon).
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
